import React, { useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";

/**
 * Reusable confirm modal for destructive actions.
 *
 * Props:
 *   • open: boolean
 *   • title: string
 *   • body: string|ReactNode
 *   • confirmLabel: string (default "Підтвердити")
 *   • cancelLabel: string (default "Скасувати")
 *   • danger: boolean (red confirm button)
 *   • loading: boolean (disables buttons + shows spinner text)
 *   • onConfirm: () => void
 *   • onClose: () => void
 *   • testidPrefix: string (for data-testid)
 */
const ConfirmModal = ({
    open,
    title,
    body,
    confirmLabel = "Підтвердити",
    cancelLabel = "Скасувати",
    danger = false,
    loading = false,
    onConfirm,
    onClose,
    testidPrefix = "confirm-modal",
}) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape" && !loading) onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, loading, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => !loading && onClose?.()}
            data-testid={`${testidPrefix}-backdrop`}
        >
            <div
                className="glass rounded-2xl border border-border w-full max-w-md p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testidPrefix}-card`}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        {danger && (
                            <div className="w-10 h-10 rounded-full bg-error/15 border border-error/30 flex items-center justify-center shrink-0">
                                <AlertTriangle size={18} className="text-error-c" />
                            </div>
                        )}
                        <h3 className="font-display font-bold text-lg text-text-primary">{title}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={() => !loading && onClose?.()}
                        className="text-faint-c hover:text-text-primary transition-colors p-1 -m-1"
                        aria-label="Закрити"
                        disabled={loading}
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-4 text-sm text-secondary-c leading-relaxed">
                    {body}
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 rounded-lg text-sm font-semibold text-secondary-c
                                   hover:text-text-primary hover:bg-surface-hover/40 transition-colors
                                   disabled:opacity-50"
                        data-testid={`${testidPrefix}-cancel`}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors
                                    disabled:opacity-60 disabled:cursor-not-allowed
                                    ${danger
                                        ? "bg-error/90 hover:bg-error text-white"
                                        : "bg-primary hover:bg-primary/90 text-white"}`}
                        data-testid={`${testidPrefix}-confirm`}
                    >
                        {loading ? "Зачекайте…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
